"use client"

import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
import { dropdownMenuItemClass, dropdownShellClass } from "@/lib/dropdown-styles"

export type AnchoredMenuItem = {
  id: string
  title: string
  subtitle?: string | null
  selected?: boolean
  destructive?: boolean
  disabled?: boolean
}

export type AnchoredMenuAnchor = { x: number; y: number; w: number; h: number }

export type AnchoredMenuPlacement = "auto" | "below"

export type AnchoredMenuPanelProps = {
  items: AnchoredMenuItem[]
  onSelect: (id: string) => void
  anchor: AnchoredMenuAnchor
  visible: boolean
  onClose?: () => void
  minWidth?: number
  maxWidth?: number
  embedded?: boolean
  placement?: AnchoredMenuPlacement
}

const ROW_HEIGHT = 44
const ANCHOR_GAP = 4
const SCREEN_EDGE_PAD = 8

export function computeAnchoredMenuLayout(
  anchor: AnchoredMenuAnchor,
  itemCount: number,
  opts?: {
    minWidth?: number
    maxWidth?: number
    screen?: { width: number; height: number }
    placement?: AnchoredMenuPlacement
  },
): { top: number; left: number; width: number } {
  const screen = opts?.screen ?? { width: window.innerWidth, height: window.innerHeight }
  const minWidth = opts?.minWidth ?? 220
  const maxWidth = opts?.maxWidth ?? screen.width - SCREEN_EDGE_PAD * 2
  const estimatedHeight = itemCount * ROW_HEIGHT + 16
  const popWidth = Math.min(Math.max(anchor.w, minWidth), maxWidth)
  const popLeft = Math.min(
    Math.max(anchor.x, SCREEN_EDGE_PAD),
    Math.max(SCREEN_EDGE_PAD, screen.width - popWidth - SCREEN_EDGE_PAD),
  )
  const placement = opts?.placement ?? "auto"
  const spaceBelow = screen.height - (anchor.y + anchor.h)
  const spaceAbove = anchor.y
  const placeAbove =
    placement === "auto" &&
    spaceBelow < estimatedHeight + ANCHOR_GAP + SCREEN_EDGE_PAD &&
    spaceAbove > spaceBelow
  const popTop = placeAbove
    ? Math.max(SCREEN_EDGE_PAD, anchor.y - estimatedHeight - ANCHOR_GAP)
    : anchor.y + anchor.h + ANCHOR_GAP

  return { top: popTop, left: popLeft, width: popWidth }
}

export function AnchoredMenuPanel({
  items,
  onSelect,
  anchor,
  visible,
  onClose,
  minWidth = 200,
  maxWidth,
  embedded = false,
  placement = "auto",
}: AnchoredMenuPanelProps) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const layout = useMemo(() => {
    if (typeof window === "undefined") {
      return { top: anchor.y, left: anchor.x, width: minWidth }
    }
    return computeAnchoredMenuLayout(anchor, items.length, { minWidth, maxWidth, placement })
  }, [anchor, items.length, minWidth, maxWidth, placement])

  if (!mounted || !visible || items.length === 0) return null

  const panel = (
    <div
      className={cn(
        dropdownShellClass("panel"),
        embedded ? "absolute z-[3] pointer-events-auto" : undefined,
      )}
      style={{ top: layout.top, left: layout.left, width: layout.width }}
      onClick={embedded ? undefined : (e) => e.stopPropagation()}
      role="menu"
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          className={cn(
            dropdownMenuItemClass("panel"),
            "w-full text-left",
            item.destructive && "text-red-400 focus:text-red-400 dark:text-red-400",
            item.disabled && "opacity-50 pointer-events-none",
          )}
          onClick={() => {
            if (item.disabled) return
            onSelect(item.id)
          }}
        >
          <span className="block truncate">{item.title}</span>
          {item.subtitle ? (
            <span className="block truncate text-sm opacity-70">{item.subtitle}</span>
          ) : null}
        </button>
      ))}
    </div>
  )

  if (embedded) return panel

  return createPortal(
    <div className="fixed inset-0 z-[10050]" onClick={onClose}>
      {panel}
    </div>,
    document.body,
  )
}
